import ArrowCircleDownIcon from "@mui/icons-material/ArrowCircleDown";
import ArrowCircleUpIcon from "@mui/icons-material/ArrowCircleUp";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import { FormControl, Grid, IconButton, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { DateTime } from "luxon";
import PropTypes from "prop-types";
import React from "react";
import { generateNCBidChoiceRows } from "../../util/bid/NonContractBidUtils";
import { generateTWUDATFTOBidChoiceRows } from "../../util/bid/TWUDATFTOBidUtils";
import {
  BID_AWARDED_TIME_OFF_TYPES,
  BID_AWARD_TYPE,
  BID_TYPE,
  NC_PTO_FTO_BID_TIME_OFF_TYPES,
  NC_PTO_GOC_HOL_BID_TIME_OFF_TYPES,
  TWU_DAT_FTO_BID_TIME_OFF_TYPES,
} from "../../util/bid/bidConstants";
import { getDefaultCalendarMonth } from "../../util/bid/freedayBidUtils";
import {
  generateMTOBidChoiceRows,
  getFirstDayOfCurrentMonth,
  getLastDayOfCurrentMonth,
} from "../../util/bid/timeOffBidUtils";
import { convert_Luxon_To_DateFns } from "../../util/dateTimeFormatter";
import { NO_CHOICES_ROW_MESSAGE } from "../../util/messageConstants";
import StarsDatePicker from "../fundamentals/StarsDatePicker";
import StarsSelectInput from "../fundamentals/StarsSelectInput";

const useStyles = makeStyles((theme) => ({
  freedayDesktop: {
    maxHeight: `calc(85vh - 290px)`,
    overflowY: "scroll",
  },
  ftoDesktop: {
    maxHeight: `calc(85vh - 300px)`,
    overflowY: "scroll",
  },
  freedayDesktopAdmin: {
    maxHeight: `calc(85vh - 337px)`,
    overflowY: "scroll",
  },
  ftoDesktopAdmin: {
    maxHeight: `calc(85vh - 410px)`,
    overflowY: "scroll",
  },
  freedayMobile: {
    overflowX: "scroll",
    maxHeight: `calc(85vh - 295px)`,
  },
  ftoMobile: {
    overflowX: "scroll",
    maxHeight: `calc(85vh - 310px)`,
  },
}));

const BidChoicesTable = (props) => {
  const classes = useStyles();

  const updateRowPosition = (initialIndex, newIndex, rows) => {
    const rowsClone = [...rows];
    const row = rowsClone.splice(initialIndex, 1)[0];
    rowsClone.splice(newIndex, 0, row);
    return rowsClone;
  };

  const renderActionsCell = (value) => {
    return (
      <Grid width={10}>
        <IconButton
          sx={{ padding: 0 }}
          onClick={() => {
            let filteredChoices;
            if (
              props.bidType === BID_TYPE.TWU_DAT_FTO ||
              props.bidType === BID_TYPE.NC_PTO_FTO ||
              props.bidType === BID_TYPE.NC_PTO_GOC_HOL
            ) {
              filteredChoices = props.selectedChoices.filter(
                (choice) => choice.date !== value.date || choice.timeOffType !== value.timeOffType
              );
            } else {
              filteredChoices = props.selectedChoices.filter((choice) => choice !== value.date);
            }
            props.setSelectedChoices(filteredChoices);
          }}
        >
          <RemoveCircleOutlineIcon color="primary" />
        </IconButton>
      </Grid>
    );
  };

  const isActionArrowUpDisabled = (value) => {
    if (
      props.bidType === BID_TYPE.TWU_DAT_FTO ||
      props.bidType === BID_TYPE.NC_PTO_FTO ||
      props.bidType === BID_TYPE.NC_PTO_GOC_HOL
    ) {
      const previousChoice = props.selectedChoices[value.id - 2]; // id values are from 1,2,3... So id-1 gives current element in array. id-2 for prev element.
      if (
        value.id === 1 ||
        value.date === "" ||
        value.timeOffType === "" ||
        (previousChoice && (previousChoice?.date === "" || previousChoice?.timeOffType === ""))
      ) {
        // disable arrow up action for the 1st row and
        // disable if we have empty values in the choice above the selected choice
        return true;
      }
      return false;
    } else if (value.id === 1 || value.date === "") {
      return true;
    }
    return false;
  };

  const isActionArrowDownDisabled = (value) => {
    if (
      props.bidType === BID_TYPE.TWU_DAT_FTO ||
      props.bidType === BID_TYPE.NC_PTO_FTO ||
      props.bidType === BID_TYPE.NC_PTO_GOC_HOL
    ) {
      const nextChoice = props.selectedChoices[value.id];
      if (
        value?.date === "" ||
        value?.timeOffType === "" ||
        (nextChoice && (nextChoice?.date === "" || nextChoice?.timeOffType === "")) ||
        !props.selectedChoices[value.id]
        // disable arrow down action for the last row and
        // disable if we have empty values in the choice below the selected choice
      ) {
        return true;
      }
      return false;
    } else if (value?.date === "" || !props.selectedChoices[value.id]) {
      return true;
    }
    return false;
  };

  const renderActionsArrowUp = (value) => {
    return (
      <Grid width={10}>
        <IconButton
          sx={{ padding: 0 }}
          disabled={isActionArrowUpDisabled(value)}
          onClick={async () => {
            if (value.id > 1 && value.date !== "") {
              let newRows = await updateRowPosition(value.id - 1, value.id - 2, props.selectedChoices);
              props.setSelectedChoices(newRows);
            }
          }}
        >
          <ArrowCircleUpIcon color="primary" />
        </IconButton>
      </Grid>
    );
  };

  const renderActionsArrowDown = (value) => {
    return (
      <Grid width={10}>
        <IconButton
          sx={{ padding: 0 }}
          disabled={isActionArrowDownDisabled(value)}
          onClick={async () => {
            if (value.date !== "" && props.selectedChoices[value.id] !== "") {
              let newRows = await updateRowPosition(value.id - 1, value.id, props.selectedChoices);
              props.setSelectedChoices(newRows);
            }
          }}
        >
          <ArrowCircleDownIcon color="primary" />
        </IconButton>
      </Grid>
    );
  };

  const handleDateChange = (date, value) => {
    const copiedChoices = [...props.selectedChoices];
    copiedChoices[value.id - 1] = date;
    props.setSelectedChoices(copiedChoices);
  };

  const hasDateSelectedForBothTimeOffType = (dayNumber) => {
    const selectedForDay = props.selectedChoices?.filter((choice) => parseInt(choice.date.slice(-2)) === dayNumber);

    const hasFTO = selectedForDay.find((choice) => choice.timeOffType === "FTO");
    const hasDAT = selectedForDay.find((choice) => choice.timeOffType === "DAT");

    return hasFTO && hasDAT;
  };

  const handleDateChangeOfChoiceWithTimeOffType = (date, value) => {
    const copiedChoices = props.selectedChoices.map((choice) => ({ ...choice }));
    copiedChoices[value.id - 1].date = date;
    copiedChoices[value.id - 1].timeOffType = ""; // Reset the time off type to avoid selecting same timeoff type for multiple days
    props.setSelectedChoices(copiedChoices);
  };

  const hasDateSelectedForNCBidBothTimeOffType = (dayNumber) => {
    const selectedForDay = props.selectedChoices?.filter((choice) => parseInt(choice.date.slice(-2)) === dayNumber);
    if (props.bidType === BID_TYPE.NC_PTO_FTO) {
      const hasFTO = selectedForDay.find((choice) => choice.timeOffType === NC_PTO_FTO_BID_TIME_OFF_TYPES.FTO);
      const hasPTO = selectedForDay.find((choice) => choice.timeOffType === NC_PTO_FTO_BID_TIME_OFF_TYPES.PTO);
      return hasFTO && hasPTO;
    } else {
      const hasPTO = selectedForDay.find((choice) => choice.timeOffType === NC_PTO_GOC_HOL_BID_TIME_OFF_TYPES.PTO);
      const hasGOCHOL = selectedForDay.find(
        (choice) => choice.timeOffType === NC_PTO_GOC_HOL_BID_TIME_OFF_TYPES.GOC_HOL
      );
      return hasPTO && hasGOCHOL;
    }
  };

  const renderDatePicker = (value) => {
    return (
      <Grid container alignItems="center">
        <Grid item>
          <FormControl fullWidth>
            <StarsDatePicker
              label="Select Date"
              date={value.date ? DateTime.fromFormat(value.date, "yyyy-MM-dd") : null}
              dateErrors={""}
              onChange={(date) =>
                props.bidType === BID_TYPE.NC_PTO_FTO ||
                props.bidType === BID_TYPE.NC_PTO_GOC_HOL ||
                props.bidType === BID_TYPE.TWU_DAT_FTO
                  ? handleDateChangeOfChoiceWithTimeOffType(date.toFormat("yyyy-MM-dd"), value)
                  : handleDateChange(date.toFormat("yyyy-MM-dd"), value)
              }
              minDate={convert_Luxon_To_DateFns(
                getFirstDayOfCurrentMonth(props.bidChoiceData.schedMonth, props.bidChoiceData.schedYear)
              )}
              toolbarPlaceholder="--"
              maxDate={convert_Luxon_To_DateFns(
                getLastDayOfCurrentMonth(props.bidChoiceData.schedMonth, props.bidChoiceData.schedYear)
              )}
              defaultCalendarMonth={getDefaultCalendarMonth(
                props.bidChoiceData.schedMonth,
                props.bidChoiceData.schedYear
              )}
              shouldDisableDate={(day) => {
                const dayNumber = day.getDate();

                // if date is employees non working day or has schedule in another temp station on that day, disable it
                let disabledDates = props.disabledDates?.find((choice) => parseInt(choice.slice(-2)) === dayNumber);

                // if date is already selected disable it
                let selectedDates = false;
                if (props.bidType === BID_TYPE.TWU_DAT_FTO) {
                  selectedDates = hasDateSelectedForBothTimeOffType(dayNumber);
                } else if (props.bidType === BID_TYPE.NC_PTO_FTO || props.bidType === BID_TYPE.NC_PTO_GOC_HOL) {
                  selectedDates = hasDateSelectedForNCBidBothTimeOffType(dayNumber);
                } else {
                  selectedDates = props.selectedChoices.find((choice) => parseInt(choice.slice(-2)) === dayNumber);
                }

                return selectedDates || disabledDates;
              }}
              isReadOnly={props.isReadOnly}
            />
          </FormControl>
        </Grid>
      </Grid>
    );
  };

  const handleTimeOffTypeChange = (event, value) => {
    const copiedChoices = props.selectedChoices.map((choice) => ({ ...choice }));
    copiedChoices[value.id - 1].timeOffType = event.target.value;
    props.setSelectedChoices(copiedChoices);
  };

  const isTimeOffTypeDisabled = (timeOffTypeOption, row) => {
    return props.selectedChoices.some(
      (choice) => choice.id !== row.id && choice.date === row.date && choice.timeOffType === timeOffTypeOption
    );
  };

  const renderTimeOffTypeSelector = (value) => {
    return (
      <Grid container alignItems="center">
        <Grid item sx={{ minWidth: "180px" }}>
          <StarsSelectInput
            label={"Select Time Off Type"}
            id="timeOffTypeSelector"
            menuMessage={"Time Off Type"}
            value={value.timeOffType ? value.timeOffType : ""}
            allOptions={
              props.bidType === BID_TYPE.TWU_DAT_FTO
                ? Object.values(TWU_DAT_FTO_BID_TIME_OFF_TYPES)
                : props.bidType === BID_TYPE.NC_PTO_FTO
                ? Object.values(NC_PTO_FTO_BID_TIME_OFF_TYPES)
                : Object.values(NC_PTO_GOC_HOL_BID_TIME_OFF_TYPES)
            }
            onChange={(event) => handleTimeOffTypeChange(event, value)}
            fullWidth={true}
            disabled={props.isReadOnly || !value.date}
            isMenuItemDisabled={(option) => isTimeOffTypeDisabled(option, value)}
            errors={
              value.date && value.timeOffType === "" && props.timeOffTypeErrors && props.timeOffTypeErrors !== ""
                ? "Time Off Type Required"
                : ""
            }
          />
        </Grid>
      </Grid>
    );
  };

  const wasChoiceUsedForAward = (row) => {
    // TWU Freeday Bid - AA
    if (
      props.selectedAwardedBid?.awardedTimeOffType === BID_AWARDED_TIME_OFF_TYPES.FREEDAY &&
      props.selectedAwardedBid?.awardType === BID_AWARD_TYPE.AUTO_AWARDED
    ) {
      return props.selectedAwardedBid.awardedDays.some((awardedDayObj) => awardedDayObj.date === row.date);
    }

    // IAM FTO Bid and IAM DAT Bid - No award type condition needed
    if (
      props.selectedAwardedBid?.awardedTimeOffType === BID_AWARDED_TIME_OFF_TYPES.FTO ||
      props.selectedAwardedBid?.awardedTimeOffType === BID_AWARDED_TIME_OFF_TYPES.DAT
    ) {
      return props.selectedAwardedBid.awardedDays.some((awardedDayObj) => awardedDayObj.date === row.date);
    }

    // TWU DAT/FTO, NC PTO/FTO, NC PTO/GOC-HOL - No award type condition needed
    if (
      props.selectedAwardedBid?.awardedTimeOffType === BID_AWARDED_TIME_OFF_TYPES.TWU_DAT_FTO ||
      props.selectedAwardedBid?.awardedTimeOffType === BID_AWARDED_TIME_OFF_TYPES.PTO_FTO ||
      props.selectedAwardedBid?.awardedTimeOffType === BID_AWARDED_TIME_OFF_TYPES.PTO_GOC_HOL
    ) {
      return props.selectedAwardedBid.awardedDays.some(
        (awardedDayObj) => awardedDayObj.date === row.date && awardedDayObj.timeOffType === row.timeOffType
      );
    }

    return false;
  };

  const getBidChoiceRows = () => {
    let bidChoiceRows = [];
    if (props.selectedChoices) {
      if (props.bidType === BID_TYPE.FREEDAY || props.bidType === BID_TYPE.FTO || props.bidType === BID_TYPE.IAM_DAT) {
        bidChoiceRows = generateMTOBidChoiceRows(props.selectedChoices, props.isReadOnly);
      } else if (props.bidType === BID_TYPE.NC_PTO_FTO || props.bidType === BID_TYPE.NC_PTO_GOC_HOL) {
        bidChoiceRows = generateNCBidChoiceRows(props.selectedChoices, props.isReadOnly);
      } else if (props.bidType === BID_TYPE.TWU_DAT_FTO) {
        bidChoiceRows = generateTWUDATFTOBidChoiceRows(props.selectedChoices, props.isReadOnly);
      }
    }
    return bidChoiceRows;
  };

  const getBidChoiceColumns = () => {
    let columns = [
      {
        field: "id",
        headerName: "Choice",
        sortable: false,
        width: 56,
        minWidth: 25,
        renderCell: (params) => {
          return <Typography sx={{ fontSize: "16px" }}>{params.row.id}</Typography>;
        },
      },
    ];

    if (!props.isReadOnly) {
      columns.push(
        {
          field: "arrowup",
          headerName: "",
          headerAlign: "left",
          align: "left",
          type: "string",
          width: 44,
          minWidth: 25,
          sortable: false,
          className: classes.blueColumn,
          renderCell: (params) => {
            return renderActionsArrowUp(params.row);
          },
        },
        {
          field: "arrowdown",
          headerName: "",
          headerAlign: "left",
          align: "left",
          type: "string",
          width: 44,
          minWidth: 25,
          sortable: false,
          className: classes.blueColumn,
          renderCell: (params) => {
            return renderActionsArrowDown(params.row);
          },
        }
      );
    }

    columns.push({
      field: "date",
      headerName: "Date",
      headerAlign: "left",
      align: "left",
      type: "string",
      width: 200,
      minWidth: 100,
      sortable: false,
      className: classes.blueColumn,
      editable: !props.isReadOnly,
      renderCell: (params) => {
        if (props.isReadOnly) {
          return (
            <>
              <Typography>{DateTime.fromFormat(params.row.date, "yyyy-MM-dd").toFormat("ccc, LL/dd/yyyy")}</Typography>
              {wasChoiceUsedForAward(params.row) && (
                <CheckCircleIcon sx={{ marginLeft: "10px", color: "primary.swaBlue" }} />
              )}
            </>
          );
        } else {
          return renderDatePicker(params.row);
        }
      },
    });

    if (
      props.bidType === BID_TYPE.TWU_DAT_FTO ||
      props.bidType === BID_TYPE.NC_PTO_FTO ||
      props.bidType === BID_TYPE.NC_PTO_GOC_HOL
    ) {
      columns.push({
        field: "timeOffType",
        headerName: "Time Off Type",
        headerAlign: "left",
        align: "left",
        type: "string",
        width: 200,
        minWidth: 100,
        sortable: false,
        className: classes.blueColumn,
        editable: !props.isReadOnly,
        renderCell: (params) => {
          if (props.isReadOnly) {
            return (
              <>
                <Typography>{params.row.timeOffType}</Typography>
                {/* {(props.selectedAwardedBid?.awardType === BID_AWARD_TYPE.AUTO_AWARDED ||
                  props.selectedAwardedBid?.awardType === IAM_FTO_BID_AWARD_TYPE.PARTIALLY_AWARDED) &&
                  props.selectedAwardedBid.awardedDays.some((day) => day.date === params.row.date) && (
                    <CheckCircleIcon sx={{ marginLeft: "10px", color: "primary.swaBlue" }} />
                  )} */}
              </>
            );
          } else {
            return renderTimeOffTypeSelector(params.row);
          }
        },
      });
    }

    if (!props.isReadOnly) {
      columns.push({
        field: "action",
        headerName: "",
        headerAlign: "left",
        align: "left",
        type: "string",
        width: 44,
        minWidth: 25,
        sortable: false,
        renderCell: (params) => {
          return renderActionsCell(params.row);
        },
      });
    }

    return columns;
  };

  return (
    <div
      style={{ width: "100%" }}
      className={
        props.bidType === BID_TYPE.FREEDAY
          ? props.isDesktopView
            ? props.isAdmin
              ? classes.freedayDesktopAdmin
              : classes.freedayDesktop
            : classes.freedayMobile
          : props.isDesktopView
          ? props.isAdmin
            ? classes.ftoDesktopAdmin
            : classes.ftoDesktop
          : classes.ftoMobile
      }
    >
      <DataGridPro
        columns={getBidChoiceColumns()}
        rows={getBidChoiceRows()}
        getRowId={(row) => row.id}
        headerHeight={50}
        getRowHeight={() =>
          props.bidType === BID_TYPE.TWU_DAT_FTO ||
          props.bidType === BID_TYPE.NC_PTO_FTO ||
          props.bidType === BID_TYPE.NC_PTO_GOC_HOL
            ? 91
            : 69
        }
        density="compact"
        localeText={NO_CHOICES_ROW_MESSAGE}
        loading={props.isBidChoicesLoading}
        disableColumnMenu={true}
        sx={{
          border: 0,
          "& .MuiDataGrid-footerContainer": {
            display: "none",
          },
          "& .MuiDataGrid-columnHeaderTitle": {
            color: "primary.swaBlue",
            fontWeight: "bold",
          },
          "& .MuiDataGrid-columnHeader": {
            padding: "0 4px 0 4px",
          },
        }}
        autoHeight={true}
      />
    </div>
  );
};

BidChoicesTable.propTypes = {
  isBidChoicesLoading: PropTypes.bool.isRequired,
  isDesktopView: PropTypes.bool.isRequired,
  selectedChoices: PropTypes.array,
  setSelectedChoices: PropTypes.func.isRequired,
  isAdmin: PropTypes.bool,
  isReadOnly: PropTypes.bool,
  disabledDates: PropTypes.array,
  selectedAwardedBid: PropTypes.object,
  bidChoiceData: PropTypes.object,
  bidType: PropTypes.string.isRequired,
  timeOffTypeErrors: PropTypes.string,
};

export default BidChoicesTable;
